import React, { useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import Icon from '../AppIcon';
import Button from './Button';

const QuickActionToolbar = () => {
  const [showQuickActions, setShowQuickActions] = useState(false);
  const location = useLocation();
  const navigate = useNavigate();

  const getContextualActions = () => {
    const currentPath = location?.pathname;
    
    switch (currentPath) {
      case '/dashboard':
        return [
          {
            label: 'Create Coupon',
            icon: 'Plus',
            action: () => navigate('/coupon-management'),
            variant: 'default',
            description: 'Create new promotional coupon'
          },
          {
            label: 'New Advertisement',
            icon: 'Megaphone',
            action: () => navigate('/advertisement-management'),
            variant: 'outline',
            description: 'Launch new ad campaign'
          },
          {
            label: 'View Feedback',
            icon: 'MessageSquare',
            action: () => navigate('/customer-feedback-management'),
            variant: 'outline',
            description: 'Check customer feedback'
          },
          {
            label: 'Update Store Info',
            icon: 'Store',
            action: () => navigate('/store-information-management'),
            variant: 'ghost',
            description: 'Manage store details'
          }
        ];
      
      case '/coupon-management':
        return [
          {
            label: 'Create Coupon',
            icon: 'Plus',
            action: () => console.log('Create new coupon'),
            variant: 'default',
            description: 'Add new promotional coupon'
          },
          {
            label: 'Bulk Import',
            icon: 'Upload',
            action: () => console.log('Bulk import coupons'),
            variant: 'outline',
            description: 'Import multiple coupons'
          },
          {
            label: 'Export Data',
            icon: 'Download',
            action: () => console.log('Export coupon data'),
            variant: 'ghost',
            description: 'Download coupon report'
          }
        ];
      
      case '/advertisement-management':
        return [
          {
            label: 'New Campaign',
            icon: 'Plus',
            action: () => console.log('Create new campaign'),
            variant: 'default',
            description: 'Launch new ad campaign'
          },
          {
            label: 'Analytics',
            icon: 'BarChart3',
            action: () => console.log('View analytics'),
            variant: 'outline',
            description: 'View campaign performance'
          },
          {
            label: 'Schedule Ads',
            icon: 'Calendar',
            action: () => console.log('Schedule advertisements'),
            variant: 'ghost',
            description: 'Plan future campaigns'
          }
        ];
      
      case '/customer-feedback-management':
        return [
          {
            label: 'Respond to Feedback',
            icon: 'Reply',
            action: () => console.log('Respond to feedback'),
            variant: 'default',
            description: 'Reply to customer feedback'
          },
          {
            label: 'Generate Report',
            icon: 'FileText',
            action: () => console.log('Generate feedback report'),
            variant: 'outline',
            description: 'Create feedback summary'
          },
          {
            label: 'Filter Reviews',
            icon: 'Filter',
            action: () => console.log('Filter feedback'),
            variant: 'ghost',
            description: 'Sort and filter feedback'
          }
        ];
      
      default:
        return [];
    }
  };

  const contextualActions = getContextualActions();

  if (contextualActions?.length === 0) {
    return null;
  }

  return (
    <div className="mb-6">
      {/* Desktop Quick Actions */}
      <div className="hidden md:flex items-center justify-between bg-card border border-border rounded-lg p-4 shadow-subtle">
        <div className="flex items-center space-x-2">
          <Icon name="Zap" size={18} className="text-accent" />
          <span className="text-sm font-medium text-foreground">Quick Actions</span>
        </div>
        
        <div className="flex items-center space-x-2">
          {contextualActions?.slice(0, 3)?.map((action, index) => (
            <Button
              key={index}
              variant={action?.variant}
              size="sm"
              onClick={action?.action}
              iconName={action?.icon}
              iconPosition="left"
              className="transition-micro"
            >
              {action?.label}
            </Button>
          ))}
          
          {contextualActions?.length > 3 && (
            <div className="relative">
              <Button
                variant="ghost"
                size="sm"
                onClick={() => setShowQuickActions(!showQuickActions)}
                iconName="MoreHorizontal"
              >
                More
              </Button>
              
              {showQuickActions && (
                <div className="absolute top-full right-0 mt-1 w-56 bg-popover border border-border rounded-md shadow-elevated z-1100">
                  <div className="py-1">
                    {contextualActions?.slice(3)?.map((action, index) => (
                      <button
                        key={index}
                        onClick={() => {
                          action?.action();
                          setShowQuickActions(false);
                        }}
                        className="flex items-center space-x-3 w-full px-3 py-2 text-sm text-popover-foreground hover:bg-muted transition-micro"
                      >
                        <Icon name={action?.icon} size={16} />
                        <div className="text-left">
                          <div className="font-medium">{action?.label}</div>
                          <div className="text-xs text-muted-foreground">{action?.description}</div>
                        </div>
                      </button>
                    ))}
                  </div>
                </div>
              )}
            </div>
          )}
        </div>
      </div>
      {/* Mobile Quick Actions */}
      <div className="md:hidden">
        <div className="bg-card border border-border rounded-lg p-4 shadow-subtle">
          <div className="flex items-center justify-between mb-3">
            <div className="flex items-center space-x-2">
              <Icon name="Zap" size={18} className="text-accent" />
              <span className="text-sm font-medium text-foreground">Quick Actions</span>
            </div>
            <Button
              variant="ghost"
              size="sm"
              onClick={() => setShowQuickActions(!showQuickActions)}
              iconName={showQuickActions ? "ChevronUp" : "ChevronDown"}
            />
          </div>
          
          {showQuickActions && (
            <div className="space-y-2">
              {contextualActions?.map((action, index) => (
                <Button
                  key={index}
                  variant={action?.variant}
                  size="sm"
                  onClick={action?.action}
                  iconName={action?.icon}
                  iconPosition="left"
                  fullWidth
                  className="justify-start"
                >
                  <div className="text-left">
                    <div>{action?.label}</div>
                    <div className="text-xs opacity-75">{action?.description}</div>
                  </div>
                </Button>
              ))}
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default QuickActionToolbar;